import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { XMarkIcon, FunnelIcon, TrashIcon, CheckIcon, PlusIcon } from '@heroicons/react/24/outline';

const FilterPopup = ({ 
  show, 
  onClose, 
  title, 
  filterBuilder, 
  setFilterBuilder, 
  filterRules, 
  setFilterRules, 
  onAddRule, 
  onRemoveRule, 
  onApply,
  onClearAll,
  fieldOptions,
  dropdownData,
  currentData = []
}) => {
  // Prevent body scroll when sidebar is open
  useEffect(() => {
    if (show) {
      document.body.style.overflow = 'hidden';
      const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
      window.addEventListener('keydown', handleEscape);
      return () => {
        document.body.style.overflow = 'unset';
        window.removeEventListener('keydown', handleEscape);
      };
    }
  }, [show, onClose]);

  if (!show) return null;

  const renderValueInputs = () => {
    const { field, op } = filterBuilder;

    // Amount range inputs
    if (field === 'amount_rs' || field === 'amount_usd') {
      return (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Min Amount</label>
            <input 
              type="number" 
              value={filterBuilder.min} 
              onChange={(e) => setFilterBuilder(prev => ({ ...prev, min: e.target.value }))} 
              className="w-full px-4 py-2.5 bg-white border border-gray-200 text-gray-900 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all" 
              placeholder="0"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Max Amount</label>
            <input 
              type="number" 
              value={filterBuilder.max} 
              onChange={(e) => setFilterBuilder(prev => ({ ...prev, max: e.target.value }))} 
              className="w-full px-4 py-2.5 bg-white border border-gray-200 text-gray-900 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all" 
              placeholder="999999"
            />
          </div>
        </div>
      );
    }

    // Month selection with FY year selector
    if (field === 'reflecting_month') {
      const availableFYYears = getAvailableFYYears();
      const availableMonths = getAvailableMonthsForFY(filterBuilder.fyYear);
      
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Financial Year</label>
            <select 
              value={filterBuilder.fyYear} 
              onChange={(e) => setFilterBuilder(prev => ({ ...prev, fyYear: e.target.value, value: '' }))} 
              className="w-full px-4 py-2.5 bg-white border border-gray-200 text-gray-900 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all"
            >
              <option value="" className="text-gray-900">Select FY Year</option>
              {availableFYYears.map(year => (
                <option key={year} value={year} className="text-gray-900">FY {year}-{(parseInt(year) + 1).toString().slice(-2)}</option>
              ))}
            </select>
          </div>
          
          {filterBuilder.fyYear && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Month</label>
              <select 
                value={filterBuilder.value} 
                onChange={(e) => setFilterBuilder(prev => ({ ...prev, value: e.target.value }))} 
                className="w-full px-4 py-2.5 bg-white border border-gray-200 text-gray-900 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all"
              >
                <option value="" className="text-gray-900">Select Month</option>
                {availableMonths.map(month => (
                  <option key={month.value} value={month.value} className="text-gray-900">{month.label}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      );
    }

    // Boolean field (request_inward)
    if (field === 'request_inward') {
      return (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Request Inward</label>
          <select 
            value={filterBuilder.yesno} 
            onChange={(e) => setFilterBuilder(prev => ({ ...prev, yesno: e.target.value }))} 
            className="w-full px-4 py-2.5 bg-white border border-gray-200 text-gray-900 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all"
          >
            <option value="true" className="text-gray-900">Yes</option>
            <option value="false" className="text-gray-900">No</option>
          </select>
        </div>
      );
    }

    // Dropdown fields
    if (['source', 'bank', 'reflecting_bank', 'category', 'sub_category'].includes(field) && op !== 'is_any') {
      const options = getDropdownOptions(field);
      return (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Select Value</label>
          <select 
            value={filterBuilder.value} 
            onChange={(e) => setFilterBuilder(prev => ({ ...prev, value: e.target.value }))} 
            className="w-full px-4 py-2.5 bg-white border border-gray-200 text-gray-900 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all"
          >
            <option value="" className="text-gray-900">Select {field.replace(/_/g, ' ')}</option>
            {options.map(option => (
              <option key={option.id || option} value={option.name || option} className="text-gray-900">
                {option.name || option}
              </option>
            ))}
          </select>
        </div>
      );
    }

    // Text fields
    if (['remark', 'expense', 'vendor', 'mode'].includes(field) && op !== 'is_any') {
      return (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            {op === 'contains' ? 'Text contains' : op === 'not_contains' ? 'Text does not contain' : 'Value'}
          </label>
          <input 
            type="text" 
            value={filterBuilder.value} 
            onChange={(e) => setFilterBuilder(prev => ({ ...prev, value: e.target.value }))} 
            className="w-full px-4 py-2.5 bg-white border border-gray-200 text-gray-900 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all" 
            placeholder={`Enter ${field.replace(/_/g, ' ')}`}
          />
        </div>
      );
    }

    // Multi-select for "is_any"
    if (op === 'is_any') {
      const options = getDropdownOptions(field);
      return (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Select Multiple Values</label>
          <div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto p-3 border border-gray-100 rounded-2xl bg-white">
            {options.map(option => {
              const val = option.name || option;
              return (
                <label key={option.id || option} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 cursor-pointer transition-all">
                  <input 
                    type="checkbox" 
                    checked={Array.isArray(filterBuilder.values) && filterBuilder.values.includes(val)} 
                    onChange={(e) => {
                      setFilterBuilder(prev => {
                        const currentValues = Array.isArray(prev.values) ? prev.values : [];
                        if (e.target.checked) {
                          return { ...prev, values: [...currentValues, val] };
                        } else {
                          return { ...prev, values: currentValues.filter(v => v !== val) };
                        }
                      });
                    }} 
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">{val}</span>
                </label>
              );
            })}
          </div>
        </div>
      );
    }

    return null;
  };

  const getAvailableFYYears = () => {
    const months = dropdownData.reflectingMonths || [];
    const fyYears = new Set();
    months.forEach(monthValue => {
      const date = new Date(monthValue);
      const month = date.getMonth() + 1;
      const year = date.getFullYear();
      const fyYear = month >= 4 ? year : year - 1;
      fyYears.add(fyYear.toString());
    });
    return Array.from(fyYears).sort((a, b) => parseInt(b) - parseInt(a));
  };

  const getAvailableMonthsForFY = (fyYear) => {
    if (!fyYear) return [];
    const months = dropdownData.reflectingMonths || [];
    const fyStartYear = parseInt(fyYear);
    const fyEndYear = fyStartYear + 1;
    const fyMonths = months.filter(monthValue => {
      const date = new Date(monthValue);
      const month = date.getMonth() + 1;
      const year = date.getFullYear();
      if (month >= 4 && year === fyStartYear) return true;
      if (month <= 3 && year === fyEndYear) return true;
      return false;
    });
    return fyMonths.map(monthValue => {
      const date = new Date(monthValue);
      return {
        value: monthValue,
        label: date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' })
      };
    }).sort((a, b) => new Date(a.value) - new Date(b.value));
  };

  const getDropdownOptions = (field) => {
    switch (field) {
      case 'source': return dropdownData.inflowSources || [];
      case 'bank': return dropdownData.bankAccounts || [];
      case 'reflecting_bank': return dropdownData.reflectingBanks || [];
      case 'category': return dropdownData.outflowCategories || [];
      case 'sub_category': return dropdownData.outflowSubcategories || [];
      case 'expense': return dropdownData.expenses || [];
      case 'mode': return dropdownData.modes || [];
      case 'vendor': return dropdownData.vendors || [];
      default: return [];
    }
  };

  const getOperatorOptions = (field) => {
    if (['source', 'bank', 'reflecting_bank', 'category', 'sub_category'].includes(field)) {
      return [{ value: 'is', label: 'is' }, { value: 'is_not', label: 'is not' }, { value: 'is_any', label: 'is any of' }];
    }
    if (['remark', 'expense', 'mode', 'vendor'].includes(field)) {
      return [{ value: 'contains', label: 'contains' }, { value: 'not_contains', label: 'does not contain' }];
    }
    if (field === 'reflecting_month' || field === 'request_inward') return [{ value: 'is', label: 'is' }];
    if (field === 'amount_rs' || field === 'amount_usd') return [{ value: 'is', label: 'range' }];
    return [];
  };

  const formatRuleDisplay = (rule) => {
    if (rule.field === 'amount_rs' || rule.field === 'amount_usd') return `${rule.field.replace(/_/g, ' ')}: ${rule.min || 0} - ${rule.max || '∞'}`;
    if (rule.op === 'is_any' && rule.values) return `${rule.field.replace(/_/g, ' ')} is any of [${rule.values.join(', ')}]`;
    return `${rule.field.replace(/_/g, ' ')} ${rule.op || 'is'} ${rule.value || ''}`;
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[10000] overflow-hidden">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity duration-300" 
        onClick={onClose}
      />
      
      {/* Sidebar Panel */}
      <div className="absolute inset-y-0 right-0 w-full max-w-md bg-white shadow-2xl flex flex-col transform transition-transform duration-300 ease-in-out">
        <div className="p-6 flex flex-col h-full">
          {/* Header */}
          <div className="flex justify-between items-center mb-8 pb-4 border-b border-gray-50">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-blue-600 rounded-2xl shadow-lg shadow-blue-100">
                <FunnelIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">{title}</h3>
                <p className="text-sm text-gray-500">Advanced data filtering</p>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-xl text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XMarkIcon className="w-7 h-7" />
            </button>
          </div>

          {/* Sidebar Content */}
          <div className="flex-1 overflow-y-auto space-y-8 pr-2 custom-scrollbar">
            {/* Filter Builder Section */}
            <div className="space-y-6 bg-slate-50/50 p-6 rounded-3xl border border-slate-100">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Target Field</label>
                  <select 
                    value={filterBuilder.field} 
                    onChange={(e) => {
                      const newField = e.target.value;
                      const defaultOp = ['remark', 'expense', 'vendor', 'mode'].includes(newField) ? 'contains' : 'is';
                      setFilterBuilder(prev => ({ 
                        ...prev, field: newField, op: defaultOp, value: '', values: [], min: '', max: '', yesno: 'true', fyYear: '' 
                      }));
                    }} 
                    className="w-full px-4 py-2.5 bg-white border border-gray-200 text-gray-900 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                  >
                    {fieldOptions.map(option => (
                      <option key={option.value} value={option.value} className="text-gray-900">{option.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Operator</label>
                  <select 
                    value={filterBuilder.op} 
                    onChange={(e) => setFilterBuilder(prev => ({ ...prev, op: e.target.value, value: '', values: [] }))} 
                    className="w-full px-4 py-2.5 bg-white border border-gray-200 text-gray-900 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                  >
                    {getOperatorOptions(filterBuilder.field).map(option => (
                      <option key={option.value} value={option.value} className="text-gray-900">{option.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {renderValueInputs()}

              <div className="flex gap-3">
                <button 
                  onClick={onAddRule} 
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3.5 bg-blue-600 text-white font-bold rounded-2xl hover:bg-blue-700 shadow-lg shadow-blue-100 transition-all active:scale-95"
                >
                  <PlusIcon className="w-5 h-5" />
                  Add Condition
                </button>
                <button 
                  onClick={() => {
                    setFilterRules([]);
                    if (onClearAll) onClearAll();
                  }} 
                  className="flex items-center justify-center gap-2 px-5 py-3.5 bg-white border border-gray-200 text-gray-600 font-bold rounded-2xl hover:bg-gray-50 transition-all"
                >
                  <TrashIcon className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Active Filters Section */}
            {filterRules.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center justify-between px-2">
                  <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wider">Active Filters</h4>
                  <span className="px-2.5 py-1 bg-blue-100 text-blue-700 text-xs font-black rounded-full">{filterRules.length}</span>
                </div>
                <div className="space-y-3">
                  {filterRules.map((rule, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-white px-4 py-4 rounded-2xl border border-gray-100 shadow-sm group hover:border-blue-100 transition-all">
                      <div className="flex flex-col">
                        <span className="text-[10px] font-black text-blue-500 uppercase tracking-widest mb-0.5">{rule.field.replace(/_/g, ' ')}</span>
                        <span className="text-sm text-gray-800 font-bold">{formatRuleDisplay(rule).split(':').slice(1).join(':') || formatRuleDisplay(rule)}</span>
                      </div>
                      <button 
                        onClick={() => onRemoveRule(idx)} 
                        className="p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Footer Buttons */}
          <div className="mt-8 pt-6 border-t border-gray-100 grid grid-cols-1 gap-4">
            <button 
              onClick={onApply} 
              className="w-full flex items-center justify-center gap-3 px-8 py-5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-black rounded-2xl hover:from-blue-700 hover:to-indigo-700 shadow-xl shadow-blue-100 transition-all transform active:scale-[0.98]"
            >
              <CheckIcon className="w-6 h-6" />
              Apply Filter Parameters
            </button>
            <button 
              onClick={onClose} 
              className="w-full py-4 text-gray-500 font-bold hover:text-gray-800 transition-colors"
            >
              Continue without applying
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default FilterPopup;
